import React, { useEffect } from 'react';
import { useIdentity } from '@workspace/identity-react';
import { Spinner, Button, makeStyles, tokens } from '@fluentui/react-components';

interface AuthWrapperProps {
  children: React.ReactNode;
}

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    gap: tokens.spacingVerticalL,
  },
  errorContainer: {
    padding: tokens.spacingVerticalL,
    textAlign: 'center',
  },
  errorDetails: {
    marginTop: tokens.spacingVerticalM,
    padding: tokens.spacingVerticalM,
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: tokens.borderRadiusMedium,
    fontSize: tokens.fontSizeBase200,
    fontFamily: 'monospace',
    maxWidth: '600px',
    overflow: 'auto',
  }
});

const AuthWrapper: React.FC<AuthWrapperProps> = ({ children }) => {
  const styles = useStyles();
  const { isAuthenticated, isLoading, signinRedirect, status, error, user } = useIdentity();

  // Debug logging
  useEffect(() => {
    console.log('Auth State:', { isAuthenticated, isLoading, status, error, user });
    console.log('Current URL:', window.location.href);
    
    // Check if we have auth params in URL
    const urlParams = new URLSearchParams(window.location.search);
    const hasCode = urlParams.has('code');
    const hasState = urlParams.has('state');
    console.log('URL has auth params:', { hasCode, hasState });
  }, [isAuthenticated, isLoading, status, error, user]);

  if (isLoading || status === 'loading') {
    return (
      <div className={styles.container}>
        <Spinner size="large" label="Loading..." />
        <p>Checking authentication status...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.errorContainer}>
          <h2>Authentication Error</h2>
          <p>There was a problem with authentication.</p>
          <div className={styles.errorDetails}>
            {error.message || 'Unknown error occurred'}
          </div>
          <Button 
            appearance="primary" 
            onClick={() => window.location.reload()}
            style={{ marginTop: '20px' }}
          >
            Reload Page
          </Button>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className={styles.container}>
        <h2>Sign In Required</h2>
        <Button 
          appearance="primary" 
          size="large"
          onClick={() => {
            console.log('Starting signin redirect...');
            signinRedirect();
          }}
        >
          Sign In
        </Button>
      </div>
    );
  }

  return <>{children}</>;
};

export default AuthWrapper;